import { useQuery } from '@tanstack/react-query'
import { v1Client } from '@renderer/chat/api/v1Client'
import { useAuth } from '@renderer/chat/providers'
import { useCanViewBraintrustLinks } from './braintrust-span-link'

export interface ConversationBraintrustSpans {
  spans: Array<{ messageId: string; permalink: string }>
  latest: string | null
}

export function pickBraintrustLink(
  data: ConversationBraintrustSpans | null | undefined,
  messageId: string | undefined,
): string | null {
  if (!data) return null
  if (!messageId) return data.latest
  return data.spans.find((span) => span.messageId === messageId)?.permalink ?? null
}

/**
 * Staff-only Braintrust permalink for a conversation: the given message's span,
 * or the latest turn's when no message is named.
 */
export function useConversationBraintrustLink(conversationId: string | undefined, messageId?: string): string | null {
  const { getAuthToken } = useAuth()
  const canViewBraintrustLinks = useCanViewBraintrustLinks()
  const { data } = useQuery({
    queryKey: ['conversation-braintrust-spans', conversationId],
    queryFn: async (): Promise<ConversationBraintrustSpans | null> => {
      if (!conversationId) return null
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Missing auth token for Braintrust link')
      }

      const result = await v1Client.GET('/debug/share-to-braintrust', {
        params: { query: { target: conversationId, type: 'chat' } },
        headers: { Authorization: `Bearer ${token}` },
      })
      if (result.error) {
        if (result.response.status === 403 || result.response.status === 404) {
          return null
        }
        throw new Error('Braintrust link lookup failed')
      }
      if (result.data.type !== 'chat') return null
      return {
        spans: result.data.spans.map(({ messageId, permalink }) => ({ messageId, permalink })),
        latest: result.data.latest?.permalink ?? null,
      }
    },
    enabled: canViewBraintrustLinks && !!conversationId,
    // Spans reach Postgres on the chat's inactivity alarm, so a missing link resolves again later.
    staleTime: (query) => (pickBraintrustLink(query.state.data, messageId) ? Infinity : 30_000),
  })

  return canViewBraintrustLinks ? pickBraintrustLink(data, messageId) : null
}
